import React, { useEffect, useState, useCallback } from 'react';
import { Trash2, Download, FileText, Upload } from 'lucide-react';
import Modal from '../components/Common/Modal';
import { toast } from 'react-toastify';
import CustomSelect from '../components/Common/CustomSelect';
import {
  MedicalDocument,
  getMedicalDocumentsByPatient,
  uploadMedicalDocument,
  deleteMedicalDocument,
  viewMedicalDocument,
} from '../services/medicalDocumentService';

interface MedicalDocumentsTabProps {
  patientId: string;
  canUpload: boolean;
  canDelete: boolean;
}

const documentTypes = [
  'Pathology Report',
  'Radiology Report',
  'Laboratory Result',
  'Treatment Record',
  'Clinical File',
  'Referral Letter',
  'Discharge Summary',
  'Consent Form',
  'Other',
];

const MedicalDocumentsTab: React.FC<MedicalDocumentsTabProps> = ({
  patientId,
  canUpload,
  canDelete,
}) => {
  const [documents, setDocuments] = useState<MedicalDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedDocId, setSelectedDocId] = useState<number | null>(null);
  const [uploading, setUploading] = useState(false);

  const [formTitle, setFormTitle] = useState('');
  const [formType, setFormType] = useState('');
  const [formNotes, setFormNotes] = useState('');
  const [formFile, setFormFile] = useState<File | null>(null);

  const fetchDocuments = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getMedicalDocumentsByPatient(patientId);
      setDocuments(data);
    } catch (error) {
      console.error('Error fetching documents:', error);
      toast.error('Failed to load documents');
    } finally {
      setLoading(false);
    }
  }, [patientId]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  const resetForm = () => {
    setFormTitle('');
    setFormType('');
    setFormNotes('');
    setFormFile(null);
  };

  const handleUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formTitle || !formType || !formFile) {
      toast.error('Title, document type, and file are required');
      return;
    }
    try {
      setUploading(true);
      await uploadMedicalDocument({
        patient_id: patientId,
        title: formTitle,
        document_type: formType,
        notes: formNotes || undefined,
        file: formFile,
      });
      toast.success('Document uploaded successfully');
      setShowUploadModal(false);
      resetForm();
      fetchDocuments();
    } catch (error) {
      console.error('Error uploading document:', error);
      toast.error('Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedDocId) return;
    try {
      await deleteMedicalDocument(selectedDocId);
      toast.success('Document deleted');
      setDeleteModalOpen(false);
      setSelectedDocId(null);
      fetchDocuments();
    } catch (error) {
      console.error('Error deleting document:', error);
      toast.error('Failed to delete document');
    }
  };

  const formatFileSize = (bytes?: number | null): string => {
    if (!bytes || bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getDocTypeColor = (type: string) => {
    switch (type?.toLowerCase()) {
      case 'pathology report':
        return 'bg-purple-100 text-purple-800';
      case 'radiology report':
        return 'bg-blue-100 text-blue-800';
      case 'laboratory result':
        return 'bg-green-100 text-green-800';
      case 'treatment record':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-neutral-100 text-neutral-800';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-lg font-semibold text-neutral-900">Medical Documents</h3>
        {canUpload && (
          <button onClick={() => setShowUploadModal(true)} className="btn-primary flex items-center space-x-2">
            <Upload className="h-4 w-4" />
            <span>Upload Document</span>
          </button>
        )}
      </div>

      {documents.length === 0 ? (
        <div className="card text-center py-8">
          <FileText className="h-10 w-10 text-neutral-400 mx-auto mb-3" />
          <p className="text-neutral-600">No medical documents found.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {documents.map((doc) => (
            <div key={doc.id} className="card">
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-3 flex-1">
                  <div className="w-10 h-10 bg-neutral-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <FileText className="h-5 w-5 text-neutral-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2 mb-1">
                      <h4 className="font-medium text-neutral-900 truncate">{doc.title}</h4>
                      <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${getDocTypeColor(doc.document_type)}`}>
                        {doc.document_type}
                      </span>
                    </div>
                    <div className="text-sm text-neutral-500 space-x-3">
                      <span>{formatFileSize(doc.file_size)}</span>
                      <span>Uploaded {new Date(doc.createdAt).toLocaleDateString()}</span>
                    </div>
                    {doc.notes && (
                      <p className="mt-1 text-sm text-neutral-600">{doc.notes}</p>
                    )}
                  </div>
                </div>
                <div className="flex space-x-2 ml-4">
                  <button
                    onClick={async () => {
                      try {
                        const url = await viewMedicalDocument(doc.id);
                        window.open(url, "_blank", "noopener,noreferrer");
                      } catch (err: any) {
                        toast.error(err.message || "Failed to open document");
                      }
                    }}
                    className="text-primary-600 hover:text-primary-900"
                    title="View Document"
                  >
                    <Download className="h-4 w-4" />
                  </button>
                  {canDelete && (
                    <button
                      onClick={() => { setSelectedDocId(doc.id); setDeleteModalOpen(true); }}
                      className="text-danger-600 hover:text-danger-800"
                      title="Delete"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Upload Modal */}
      <Modal title="Upload Medical Document" isOpen={showUploadModal} onClose={() => { setShowUploadModal(false); resetForm(); }} widthClassName="max-w-2xl">
        <form onSubmit={handleUpload} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-neutral-700">Document Title *</label>
              <input value={formTitle} onChange={(e) => setFormTitle(e.target.value)} className="input-field mt-1" placeholder="e.g., CT Scan Report - Chest" />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700">Document Type *</label>
              <CustomSelect value={formType} onChange={setFormType} placeholder="Select type" options={documentTypes.map((t) => ({ value: t, label: t }))} className="mt-1" />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700">File *</label>
              <input
                type="file"
                onChange={(e) => setFormFile(e.target.files?.[0] || null)}
                className="input-field mt-1"
                accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.txt,.dcm"
              />
              <p className="text-xs text-neutral-500 mt-1">PDF, JPEG, PNG, DICOM, DOC, DOCX, TXT (max 25MB)</p>
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-neutral-700">Notes</label>
              <textarea value={formNotes} onChange={(e) => setFormNotes(e.target.value)} className="input-field mt-1" rows={2} placeholder="Optional notes..." />
            </div>
          </div>
          <div className="flex justify-end space-x-3 pt-4 border-t border-neutral-200">
            <button type="button" onClick={() => { setShowUploadModal(false); resetForm(); }} className="btn-secondary">Cancel</button>
            <button type="submit" disabled={uploading} className="btn-primary">
              {uploading ? 'Uploading...' : 'Upload Document'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation */}
      <Modal title="Delete Document" isOpen={deleteModalOpen} onClose={() => setDeleteModalOpen(false)} widthClassName="max-w-md">
        <p className="text-neutral-700 mb-6">Are you sure you want to delete this document? This action cannot be undone.</p>
        <div className="flex justify-end space-x-3">
          <button onClick={() => setDeleteModalOpen(false)} className="btn-secondary">Cancel</button>
          <button onClick={handleDelete} className="btn-danger">Delete</button>
        </div>
      </Modal>
    </div>
  );
};

export default MedicalDocumentsTab;
